import { type ComponentPropsWithoutRef, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'

import { ThemeContext } from 'js/theme/SubTheme'
import type { AccessibleName } from 'js/types/AccessibleName'

import styles from './Drawer.module.css'
import {
    closeOnBackdropOrLink,
    closeOnSubmit,
    DRAWER_OPEN_EVENT,
} from './drawerActions'

type DialogProps = ComponentPropsWithoutRef<'dialog'>

export type DrawerProps = AccessibleName &
    Required<Pick<DialogProps, 'children'>> & {
        /** What `openDrawer` and `closeDrawer` use to find the drawer */
        id: string
    }

/** A panel that slides in from the left over the page, as a native modal
 * dialog. Open and close it with `openDrawer(id)` and `closeDrawer(id)`. */
export const Drawer = ({
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    children,
}: DrawerProps) => {
    // State rather than a ref, so the popups re-render once the dialog exists
    const [drawer, setDrawer] = useState<HTMLDialogElement>(null)
    // The children mount when the drawer first opens, and stay mounted after
    const [hasOpened, setHasOpened] = useState(false)

    useEffect(() => {
        if (!drawer || hasOpened) {
            return
        }

        // Render synchronously, so the children exist before showModal moves focus
        const mount = () => flushSync(() => setHasOpened(true))

        drawer.addEventListener(DRAWER_OPEN_EVENT, mount)
        return () => drawer.removeEventListener(DRAWER_OPEN_EVENT, mount)
    }, [drawer, hasOpened])

    return (
        // Keyboard users close the dialog with Escape, and Enter on a link fires a click
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
        <dialog
            ref={setDrawer}
            id={id}
            className={styles.drawer}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            onClick={closeOnBackdropOrLink}
            onSubmit={closeOnSubmit}
        >
            {/* FIXME(beheer#28632): ThemeContext is not used in this component,
                but it is used in the Tooltip and Popover components. Needs refactoring.
                Despite its name, ThemeContext (from SubTheme) tells Tooltip and
                Popover where to portal their popup, else they use document.body.
                The open drawer is in the top layer, above everything in body, so
                popups from inside it have to render inside the dialog. */}
            {hasOpened && (
                <ThemeContext.Provider value={{ container: drawer }}>
                    {children}
                </ThemeContext.Provider>
            )}
        </dialog>
    )
}
